/* eslint-disable max-len */
/* eslint-disable func-names */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FormikProvider, useFormik } from 'formik';
import isNil from 'lodash/isNil';
import styled from 'styled-components';
import * as Yup from 'yup';
import { AlertContent, Breadcrumbs, BreadcrumbsItem, LayoutSidebar, PageHeader, PageHeaderContent, PageTitle } from 'components';
import { appConfig } from 'config/app';
import routeConstants, { URL } from 'routes/routeConstants';
import { selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee, setCurrentEmployeeRequest } from 'store/App';
import { selectCurrentEmployeeOrg, selectCurrentEmployeeSite } from 'store/Employers';
import { Payment, payPaymentSetRequest } from 'store/Payment';
import { getSiteBalanceRequest, selectSiteAcceptance, selectSiteAcceptanceBalance, selectSiteSettings, Site } from 'store/Site';
import { getPaymentSetRequest, TipAcceptance } from 'store/TipAcceptance';
import { IStore } from 'store/types';
import { EmployeeType, IAcceptancePayout, IApiErrorPayloadWithCode, ITipAcceptancePaymentSetPayouts, PayoutMethodTypes, PayoutRefType } from 'types';
import { sumBy } from 'utils/arrays';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateForApi } from 'utils/date';
import { siteUsesHoldbacks } from 'utils/site';
import { MPContent } from '../components';
import { EditMode } from './EditMode';
import { ReviewMode } from './ReviewMode';
import { TipAcceptanceSideBar } from './Sidebar';
import { ITipAcceptanceValues } from './types';

dayjs.extend(utc);

interface ITipAcceptanceParams {
  paymentId: string;
  siteId: string;
  allowHoldbacks: string;
}

const PayTipError = styled(AlertContent)`
  margin: 0 auto;
  padding: 0;
  box-shadow: none;
`;

const PayTips: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const allowHoldbacksQueryParamOverride = new URLSearchParams(search).get('allowHoldbacks');
  const params = useParams<ITipAcceptanceParams>();
  const { paymentId, siteId } = params;
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentSiteAcceptance = useSelector(selectSiteAcceptance());
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const acceptanceBalance = useSelector(selectSiteAcceptanceBalance());
  const [tipAcceptancePaymentSet, setTipAcceptancePaymentSet] = useState<ITipAcceptancePaymentSetPayouts | null>(null);

  /**
   * GLOBAL FLAG THAT WILL TURN HOLDBACKS ON AND OFF
   * WITHIN THE TIP ACCEPTANCE PAYMENT FLOW, EVEN IF
   * HOLDBACKS ARE ENABLED FOR A SITE.
   *
   * NOTE:
   * this can be overwritten while in development by
   * using the `allowHoldbacks` query parameter and
   * setting it to either `true` or `false`.
   */
  const AllowHoldbacksFlag = useMemo(() => {
    if (allowHoldbacksQueryParamOverride) {
      return allowHoldbacksQueryParamOverride === 'true';
    }

    return appConfig.TIP_ACCEPTANCE.ALLOW_HOLDBACKS;
  }, [allowHoldbacksQueryParamOverride]);

  const { holdback: siteHoldbacks, perTipMax, rolePolicy: siteRolePolicy, strictTipPool, timezone } = useSelector(selectSiteSettings()) || {};

  const balanceIsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Site.GET_SITE_BALANCE_REQUEST));
  const paymentSetIsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, TipAcceptance.GET_PAYMENT_SET_REQUEST));
  const employeeSiteIsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Site.GET_SITE_REQUEST));
  const paymentIsProcessing = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.CREATE_PAYMENT_REQUEST));
  const paymentSetError = useSelector((state: IStore) => selectAPIRequestErrors(state, TipAcceptance.GET_PAYMENT_SET_REQUEST)) as IApiErrorPayloadWithCode;
  const currentOrganization = useSelector(selectCurrentEmployeeOrg);
  const [isEditing, setIsEditing] = useState(false);
  const isActiveOrg = currentOrganization && currentOrganization.sites[siteId];

  const [paymentSetDescription, setPaymentSetDescription] = useState('');
  const [paymentSetPayoutLimit, setPaymentSetPayoutLimit] = useState({
    isActive: true,
    isStrict: strictTipPool,
    value: tipAcceptancePaymentSet?.totals?.net,
  });
  const [authorizedAgreement, setAuthorizedAgreement] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setPaymentSetPayoutLimit({
      isActive: true,
      isStrict: strictTipPool,
      value: tipAcceptancePaymentSet?.totals?.net,
    });
  }, [strictTipPool, tipAcceptancePaymentSet?.totals?.net]);

  useEffect(() => {
    if (currentOrganization && siteId && !isActiveOrg) history.push(routeConstants.private.rootPath);
  }, [siteId, isActiveOrg, currentOrganization]);

  useEffect(() => {
    if (siteId) {
      dispatch(getSiteBalanceRequest(siteId));
    }
  }, [siteId, dispatch]);

  useEffect(() => {
    if (siteId && !siteHoldbacks && !employeeSiteIsLoading) {
      dispatch(setCurrentEmployeeRequest({ ...currentEmployee, siteId }));
    }
  }, [siteId, siteHoldbacks, employeeSiteIsLoading]);

  useEffect(() => {
    if (!siteId || !paymentId || (currentSiteAcceptance && !currentSiteAcceptance.enabled)) {
      history.push(routeConstants.private.paymentsOverview);
    }
  }, [history, siteId, currentSiteAcceptance]);

  // Refetch paymentSet on state change
  useEffect(() => {
    if (siteId && paymentId && siteHoldbacks && !tipAcceptancePaymentSet && !paymentSetIsLoading) {
      dispatch(
        getPaymentSetRequest(
          {
            siteId,
            paymentSetId: paymentId,
            siteHoldbacks,
          },
          (response: ITipAcceptancePaymentSetPayouts) => setTipAcceptancePaymentSet(response)
        )
      );
    }
  }, [siteId, paymentId, tipAcceptancePaymentSet, siteHoldbacks, dispatch]);

  const payoutFieldSchema = useMemo(
    () => ({
      amount: Yup.number()
        .typeError('Invalid gross')
        .required('Invalid gross')
        .test('allowedMaxAndMin', 'Invalid gross', function (value) {
          if (perTipMax < value - this.parent.holdbackAmount) return false;
          return true;
        }),
      employee: Yup.object().shape({
        holdbackAmount: Yup.number()
          .typeError('Invalid gross')
          .test('moreThanTipAmount', 'Tip amount needs to be greater than holdback', function (value) {
            if (!AllowHoldbacksFlag) return true;

            return !(this.parent.tipAmount <= value);
          }),
      }),
      job: Yup.object().shape({
        classification: Yup.string(),
        title: Yup.string(),
      }),
      note: Yup.string().max(100, 'Invalid note'),
    }),
    [perTipMax]
  );

  const changeTipValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        payouts: Yup.array()
          .of(Yup.object().shape(payoutFieldSchema))
          .test('lessThanBalance', 'Total paid cannot exceed the sum of account balance and overdraft limit', (payoutArr) => {
            const totalTips = sumBy(payoutArr, 'amount') - (AllowHoldbacksFlag ? sumBy(payoutArr, 'holdbackAmount') : 0);
            const balance = isNil(acceptanceBalance) ? 0 : (acceptanceBalance as number);
            return totalTips <= balance;
          })
          .required('Must select at least one payee'),
        description: Yup.string().max(100, 'Description cannot be more than 100 characters'),

        businessDate: Yup.string()
          .test('businessDate', 'Invalid business date', (businessDate: string) => {
            const siteDate = dayjs.max(dayjs(dayjs().tz(timezone).format('YYYY-MM-DD')), dayjs(dayjs().format('YYYY-MM-DD')));
            return dayjs(businessDate, 'MM-DD-YYYY', true).isSameOrBefore(siteDate) && dayjs(businessDate, 'MM-DD-YYYY', true).isValid();
          })
          .required(),
      }),
    [acceptanceBalance]
  );

  const onBackClick = useCallback(() => {
    history.push(routeConstants.private.paymentsAcceptance);
  }, []);

  const formik = useFormik({
    initialValues: {
      payouts: tipAcceptancePaymentSet?.payouts,
      description: paymentSetDescription,
      businessDate: dayjs(tipAcceptancePaymentSet?.paymentDate, 'YYYY-MM-DD').isValid() ? dayjs(tipAcceptancePaymentSet?.paymentDate, 'YYYY-MM-DD').format('MM-DD-YYYY') : dayjs().format('MM-DD-YYYY'),
      authorizedAgreement,
      payoutLimit: paymentSetPayoutLimit,
    } as ITipAcceptanceValues,
    onSubmit: (data: ITipAcceptanceValues) => {
      dispatch(
        payPaymentSetRequest({
          paymentDate: formatDateForApi(data.businessDate),
          description: data.description,
          refType: PayoutRefType.ACCEPTANCE,
          refId: tipAcceptancePaymentSet?.id,
          siteId,
          payouts: data.payouts.map((payout: IAcceptancePayout) => {
            const gross = payout.amount;
            const holdback = AllowHoldbacksFlag ? payout.holdbackAmount || 0 : 0;

            let job = payout.employee.sites.find((site) => site.id === siteId)?.job;

            if (!job && payout.employee.type === EmployeeType.ADMIN) {
              job = { title: 'org admin' };
            }

            return {
              currency: 'usd',
              employeeId: payout.employee.id,
              gross,
              holdback,
              job,
              net: gross - (AllowHoldbacksFlag ? holdback : 0),
              notes: payout.notes,
              refId: tipAcceptancePaymentSet.id,
              type: 'tip',
            };
          }),
          workflow: PayoutMethodTypes.STANDARD,
        })
      );
    },
    validationSchema: changeTipValidationSchema,
    validateOnBlur: true,
    enableReinitialize: true,
    validateOnChange: true,
  });

  const onUndoEditsClick = useCallback(() => {
    setIsEditing(false);
  }, []);

  const onConfirmEditsClick = useCallback(
    (values: ITipAcceptanceValues) => {
      setTipAcceptancePaymentSet({ ...tipAcceptancePaymentSet, payouts: values.payouts });
      setAuthorizedAgreement(!!values.authorizedAgreement);
      setPaymentSetDescription(values.description);
      setPaymentSetPayoutLimit({ ...values.payoutLimit });
      setIsEditing(false);
    },
    [tipAcceptancePaymentSet]
  );

  const renderContent = () => {
    const content = paymentSetError?.errorMessages?.length ? (
      <PayTipError
        title="Unable to load payments"
        message="Please go back to Tip Acceptance and choose a business day."
        rightActionLabel="View Tip Acceptance"
        onRightAction={() => history.push(routeConstants.private.paymentsOverview)}
        hideLeftAction
      />
    ) : isEditing && formik?.values?.payouts ? (
      <EditMode formik={formik} allowHoldbacks={AllowHoldbacksFlag} />
    ) : (
      <ReviewMode
        dataCy="tip-acceptance-pay-tips-review-mode"
        isLoading={paymentSetIsLoading || employeeSiteIsLoading}
        onActionClick={() => setIsEditing(true)}
        paymentSet={tipAcceptancePaymentSet}
        rolePolicy={siteRolePolicy}
        siteUsesHoldbacks={AllowHoldbacksFlag && siteUsesHoldbacks(siteHoldbacks)}
        perTipMax={perTipMax}
      />
    );

    return (
      <>
        <PageHeader>
          <Breadcrumbs>
            <BreadcrumbsItem data-cy="payments-breadcrumblink" label="Payments" to={URL.PaymentsOverview} />
            <BreadcrumbsItem data-cy="payments-breadcrumblink" label="Tip Acceptance" to={URL.PaymentsAcceptance} />
            <BreadcrumbsItem data-cy="make-payments-breadcrumblink" label="Pay Tips" />
          </Breadcrumbs>

          <PageHeaderContent>
            <PageTitle data-cy="tip-acceptance-review-site-balance" isLoading={balanceIsLoading} title={currentSite?.name} subtitle={`Acceptance Balance: ${centsToDollarsDisplay(acceptanceBalance || 0)}`} />
          </PageHeaderContent>
        </PageHeader>

        <MPContent>{content}</MPContent>
      </>
    );
  };

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <LayoutSidebar
          data-cy="tip-acceptance-pay-tips-review"
          content={renderContent()}
          fixedMobile={isEditing}
          sidebar={
            <TipAcceptanceSideBar
              formik={formik}
              isEditing={isEditing}
              isProcessing={paymentIsProcessing || paymentSetIsLoading}
              paymentSet={tipAcceptancePaymentSet}
              sidebarOpen={sidebarOpen}
              siteId={siteId}
              onBackClick={onBackClick}
              onConfirmEditsClick={onConfirmEditsClick}
              onExpand={() => setSidebarOpen(!sidebarOpen)}
              onUndoEditsClick={onUndoEditsClick}
              hideHoldbacks={!AllowHoldbacksFlag}
            />
          }
          sidebarOpen={sidebarOpen}
        />
      </form>
    </FormikProvider>
  );
};

export default PayTips;
